import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw, Expand, Phone, DollarSign, User, Ruler } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SiteMapOverlay = ({ mapImage, projectId }) => {
  const [units, setUnits] = useState([]);
  const [soldUnits, setSoldUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoneFilter, setZoneFilter] = useState('all');

  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Track the map container width so we can size unit dots proportionally
  // (otherwise on mobile a 6px dot becomes huge relative to the shrunken map)
  const mapRef = useRef(null);
  const fsMapRef = useRef(null);
  const [mapWidth, setMapWidth] = useState(0);
  const [fsMapWidth, setFsMapWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (mapRef.current) setMapWidth(mapRef.current.clientWidth);
      if (fsMapRef.current) setFsMapWidth(fsMapRef.current.clientWidth);
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    if (mapRef.current) ro.observe(mapRef.current);
    if (fsMapRef.current) ro.observe(fsMapRef.current);
    window.addEventListener('resize', updateWidth);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [imgLoaded, isFullscreen]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sitemap-units/${projectId}`, { cache: 'no-store' });
        const data = await res.json();
        if (cancelled) return;
        const onMapUnits = data.units || [];
        setUnits(onMapUnits);
        setSoldUnits(onMapUnits.filter(u => u.status === 'sold' && u.position_x != null));
      } catch (err) {
        console.error('Error fetching sitemap data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    // Refetch when tab regains focus (e.g., after editing in admin tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', fetchData);

    // Background polling every 30s so multiple viewers stay in sync
    const interval = setInterval(fetchData, 30000);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', fetchData);
      clearInterval(interval);
    };
  }, [projectId]);

  const openFullscreen = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setSelectedUnit(null);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsFullscreen(false);
    setSelectedUnit(null);
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 4));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 1));
  const resetZoom = () => { setScale(1); setPosition({ x: 0, y: 0 }); };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setScale(prev => Math.max(1, Math.min(4, prev + delta)));
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('[data-sold-marker]')) return;
    if (scale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    const maxPan = (scale - 1) * 300;
    setPosition({
      x: Math.max(-maxPan, Math.min(maxPan, newX)),
      y: Math.max(-maxPan, Math.min(maxPan, newY))
    });
  };

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (scale <= 1) setPosition({ x: 0, y: 0 });
  }, [scale]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  // Escape key closes fullscreen
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closeFullscreen(); };
    if (isFullscreen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen]);

  const handleMarkerClick = (unit) => {
    setSelectedUnit(unit);
  };

  const getTypeLabel = (type, sqm) => {
    if (type === 'A') return `Type A - ${sqm || 300}m²`;
    if (type === 'B') return `Type B - ${sqm || 203}m²`;
    if (type === 'C') return `Type C - ${sqm || 160}m²`;
    return `${sqm || ''}m²`;
  };

  const renderMarkers = (isFs = false) => {
    // Scale dot size relative to the map's actual rendered width so dots stay
    // visually proportional to the unit cells across all viewports.
    // Reference: dots looked right at ~6px when map was ~1800px wide (≈ width/300).
    const containerW = isFs ? fsMapWidth : mapWidth;
    const baseSize = containerW > 0 ? containerW / 300 : 6;
    const size = Math.max(2.5, Math.min(isFs ? 9 : 7, baseSize));
    const filteredUnits = zoneFilter === 'all' ? units : units.filter(u => u.zone === zoneFilter);
    return filteredUnits.filter(u => u.position_x != null).map((unit) => {
      const status = (unit.status || 'available').toLowerCase();
      const isSold = status === 'sold';
      const isPending = status === 'pending';
      const isCompany = status === 'company_buyer';
      const isInteractive = isSold || isPending || isCompany;
      const colorClass = isSold
        ? 'bg-red-600 cursor-pointer hover:scale-[2] hover:shadow-[0_0_8px_rgba(220,38,38,0.7)]'
        : isPending
          ? 'bg-amber-500 cursor-pointer hover:scale-[2] hover:shadow-[0_0_8px_rgba(245,158,11,0.7)]'
          : isCompany
            ? 'bg-blue-500 cursor-pointer hover:scale-[2] hover:shadow-[0_0_8px_rgba(59,130,246,0.7)]'
            : 'bg-emerald-500';
      const statusLabel = isSold ? 'Sold' : isPending ? 'Pending' : isCompany ? 'Company Buyer' : 'Available';
      return (
        <div
          key={`${unit.villa_type}-${unit.villa_number}`}
          data-sold-marker={isInteractive ? "true" : undefined}
          data-testid={`unit-marker-${unit.villa_number}`}
          onClick={isInteractive ? (e) => { e.stopPropagation(); handleMarkerClick(unit); } : undefined}
          onMouseDown={isInteractive ? (e) => e.stopPropagation() : undefined}
          role={isInteractive ? "button" : undefined}
          tabIndex={isInteractive ? 0 : undefined}
          className={`absolute rounded-full transition-all duration-200 ${colorClass}`}
          style={{
            left: `${unit.position_x}%`,
            top: `${unit.position_y}%`,
            width: `${size}px`,
            height: `${size}px`,
            transform: 'translate(-50%, -50%)',
            opacity: 0.85,
            zIndex: isInteractive ? 10 : 5,
            pointerEvents: isInteractive ? 'auto' : 'none',
          }}
          title={`Villa #${unit.villa_number} - ${statusLabel}`}
        />
      );
    });
  };

  const renderUnitDetails = (unit) => (
    <div className="space-y-3">
      <div className="flex items-center gap-3 p-3 border border-white/10 bg-white/5">
        <Ruler className="w-4 h-4 text-white/40 flex-shrink-0" />
        <div>
          <p className="text-[9px] uppercase tracking-widest text-white/40 font-light">Size</p>
          <p className="text-sm text-white font-light">{getTypeLabel(unit.villa_type, unit.sqm)}</p>
        </div>
      </div>
      {unit.price && (
        <div className="flex items-center gap-3 p-3 border border-white/10 bg-white/5">
          <DollarSign className="w-4 h-4 text-white/40 flex-shrink-0" />
          <div>
            <p className="text-[9px] uppercase tracking-widest text-white/40 font-light">Price</p>
            <p className="text-sm text-white font-light">{unit.price}</p>
          </div>
        </div>
      )}
      {unit.buyer_name && (
        <div className="flex items-center gap-3 p-3 border border-white/10 bg-white/5">
          <User className="w-4 h-4 text-white/40 flex-shrink-0" />
          <div>
            <p className="text-[9px] uppercase tracking-widest text-white/40 font-light">Buyer</p>
            <p className="text-sm text-white font-light">{unit.buyer_name}</p>
          </div>
        </div>
      )}
      {unit.phone && (
        <div className="flex items-center gap-3 p-3 border border-white/10 bg-white/5">
          <Phone className="w-4 h-4 text-white/40 flex-shrink-0" />
          <div>
            <p className="text-[9px] uppercase tracking-widest text-white/40 font-light">Phone</p>
            <p className="text-sm text-white font-light">{unit.phone}</p>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="sitemap-overlay">
      {/* Normal View */}
      <div className="relative">
        <div ref={mapRef} className="relative w-full overflow-hidden border border-white/10 bg-black">
          {!imgLoaded && !imgError && (
            <div className="flex items-center justify-center py-40">
              <div className="w-6 h-6 border border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}
          {imgError && (
            <div className="flex items-center justify-center py-40 text-white/40 text-sm">
              Failed to load map image. Please refresh the page.
            </div>
          )}
          <img
            src={mapImage}
            alt="Site Map"
            className={`w-full h-auto block select-none ${imgLoaded ? '' : 'hidden'}`}
            draggable={false}
            loading="eager"
            fetchPriority="high"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            data-testid="sitemap-image"
          />
          {imgLoaded && <div className="absolute inset-0">{renderMarkers(false)}</div>}
        </div>
        {imgLoaded && (
          <button onClick={openFullscreen} className="absolute top-2 right-2 sm:top-4 sm:right-4 p-2 sm:p-3 bg-black/60 hover:bg-black/80 text-white transition-colors z-20" data-testid="sitemap-fullscreen-btn">
            <Expand className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 py-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500" style={{ opacity: 0.85 }} />
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.12em] font-light">Available ({units.filter(u => (u.status || 'available').toLowerCase() === 'available').length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-amber-500" style={{ opacity: 0.85 }} />
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.12em] font-light">Pending ({units.filter(u => (u.status || '').toLowerCase() === 'pending').length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500" style={{ opacity: 0.85 }} />
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.12em] font-light">Company Buyers ({units.filter(u => (u.status || '').toLowerCase() === 'company_buyer').length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-red-600" style={{ opacity: 0.85 }} />
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.12em] font-light">Sold ({units.filter(u => (u.status || '').toLowerCase() === 'sold').length})</span>
        </div>
        <button onClick={openFullscreen} className="flex items-center gap-2 text-white/40 hover:text-white transition-colors">
          <Expand className="w-3 h-3" />
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.12em] font-light">Fullscreen</span>
        </button>
      </div>

      {/* Fullscreen Overlay (custom, no Radix Dialog) */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black" data-testid="sitemap-fullscreen">
          {/* Controls */}
          <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-2 sm:p-3 bg-gradient-to-b from-black via-black/80 to-transparent">
            <div className="flex items-center gap-1 bg-black/60 p-1">
              <button onClick={zoomOut} disabled={scale <= 1} className="p-1.5 hover:bg-white/10 text-white transition-colors disabled:opacity-30"><ZoomOut className="w-3.5 h-3.5" /></button>
              <span className="text-white text-[10px] font-light px-2 min-w-[45px] text-center">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} disabled={scale >= 4} className="p-1.5 hover:bg-white/10 text-white transition-colors disabled:opacity-30"><ZoomIn className="w-3.5 h-3.5" /></button>
              <button onClick={resetZoom} className="p-1.5 hover:bg-white/10 text-white transition-colors ml-1"><RotateCcw className="w-3.5 h-3.5" /></button>
            </div>
            <button onClick={closeFullscreen} className="p-1.5 bg-black/60 hover:bg-white/10 text-white transition-colors"><X className="w-4 h-4" /></button>
          </div>

          {/* Map Area */}
          <div
            className="w-full h-full flex items-center justify-center pt-12 pb-4 overflow-hidden"
            style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
          >
            <div style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out',
              transformOrigin: 'center center',
            }}>
              <div ref={fsMapRef} className="relative" style={{ width: 'min(92vw, calc((90vh - 80px) * 1.54))' }}>
                <img src={mapImage} alt="Site Map" className="w-full h-auto block select-none" draggable={false} />
                <div className="absolute inset-0">{renderMarkers(true)}</div>
              </div>
            </div>
          </div>

          {/* Side Panel */}
          {selectedUnit && (() => {
            const st = (selectedUnit.status || '').toLowerCase();
            const stLabel = st === 'sold' ? 'Sold' : st === 'pending' ? 'Pending' : st === 'company_buyer' ? 'Company Buyer' : 'Available';
            const stColor = st === 'sold' ? 'text-red-400' : st === 'pending' ? 'text-amber-400' : st === 'company_buyer' ? 'text-blue-400' : 'text-emerald-400';
            return (
              <div className="absolute top-12 right-0 bottom-0 w-72 sm:w-80 bg-[#0a0a0a]/95 backdrop-blur-sm border-l border-white/10 z-40 overflow-y-auto" data-testid="unit-side-panel">
                <div className="p-5 space-y-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-[10px] uppercase tracking-[0.2em] ${stColor} font-light mb-1`}>{stLabel}</p>
                      <h3 className="text-xl font-bold text-white">Villa #{selectedUnit.villa_number}</h3>
                    </div>
                    <button onClick={() => setSelectedUnit(null)} className="p-1.5 hover:bg-white/10 text-white/50 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                  {renderUnitDetails(selectedUnit)}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Non-fullscreen detail popup */}
      {selectedUnit && !isFullscreen && (() => {
        const st = (selectedUnit.status || '').toLowerCase();
        const stLabel = st === 'sold' ? 'Sold Unit' : st === 'pending' ? 'Pending Unit' : st === 'company_buyer' ? 'Company Buyer' : 'Available Unit';
        const stColor = st === 'sold' ? 'text-red-400' : st === 'pending' ? 'text-amber-400' : st === 'company_buyer' ? 'text-blue-400' : 'text-emerald-400';
        return (
          <Dialog open={!!selectedUnit} onOpenChange={() => setSelectedUnit(null)}>
            <DialogContent className="max-w-sm border-white/10 p-0 bg-[#0a0a0a] overflow-hidden" data-testid="unit-detail-dialog">
              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`text-[10px] uppercase tracking-[0.2em] ${stColor} font-light mb-1`}>{stLabel}</p>
                    <h3 className="text-2xl font-bold text-white">Villa #{selectedUnit.villa_number}</h3>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-4">
                  {renderUnitDetails(selectedUnit)}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
};
